/**
 * Vimeo URL helpers shared by the video players.
 *
 * Supported URL shapes:
 *   - https://vimeo.com/123456789                (canonical)
 *   - https://vimeo.com/123456789?query#fragment
 *   - https://vimeo.com/123456789/abcdef0123     (unlisted + privacy hash)
 *   - https://player.vimeo.com/video/123456789
 *   - https://vimeo.com/share/<UUID>[?query]     (new share links; resolved
 *     to a numeric id through the public oEmbed API)
 */

// Numeric id as a full path segment. The lookahead prevents matching the
// digit-prefix of a share UUID (e.g. "7" in share/7ce46a29-...).
const VIDEO_ID_PATTERN = /vimeo\.com\/(?:[\w-]+\/)*?(\d+)(?=[/?#]|$)/;

const SHARE_UUID_PATTERN =
    /vimeo\.com\/share\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})(?=[/?#]|$)/;

const UNLISTED_HASH_PATTERN = /vimeo\.com\/(\d+)\/([0-9a-zA-Z]+)(?=[/?#]|$)/;

const OEMBED_ENDPOINT = "https://vimeo.com/api/oembed.json";

export function getVimeoVideoId(url) {
    if (typeof url !== "string") return null;
    const match = url.match(VIDEO_ID_PATTERN);
    return match ? match[1] : null;
}

export function getVimeoShareUuid(url) {
    if (typeof url !== "string") return null;
    const match = url.match(SHARE_UUID_PATTERN);
    return match ? match[1] : null;
}

export function getVimeoUnlistedHash(url) {
    if (typeof url !== "string") return null;
    const match = url.match(UNLISTED_HASH_PATTERN);
    return match ? match[2] : null;
}

function extractHashFromOembed(data) {
    const html = data && typeof data.html === "string" ? data.html : "";
    const htmlMatch = html.match(/[?&](?:amp;)?h=([0-9a-zA-Z]+)/);
    if (htmlMatch) return htmlMatch[1];

    const uri = data && typeof data.uri === "string" ? data.uri : "";
    const uriMatch = uri.match(/\/videos\/\d+:([0-9a-zA-Z]+)/);
    return uriMatch ? uriMatch[1] : null;
}

/**
 * Resolve a share link (vimeo.com/share/<UUID>) to { id, hash } through the
 * public oEmbed API (no token required, CORS-enabled).
 * Returns null when the link cannot be resolved.
 */
export async function resolveVimeoShareUrl(url) {
    const uuid = getVimeoShareUuid(url);
    if (!uuid) return null;

    const shareUrl = `https://vimeo.com/share/${uuid}`;
    const endpoint = `${OEMBED_ENDPOINT}?url=${encodeURIComponent(shareUrl)}`;

    try {
        const response = await fetch(endpoint);
        if (!response.ok) {
            console.error(
                `Vimeo oEmbed returned ${response.status} for share link`,
                shareUrl
            );
            return null;
        }

        const data = await response.json();
        if (!data || !data.video_id) {
            console.error("Vimeo oEmbed response has no video_id", shareUrl);
            return null;
        }

        return {
            id: String(data.video_id),
            hash: extractHashFromOembed(data),
        };
    } catch (error) {
        console.error("Failed to resolve Vimeo share link:", error);
        return null;
    }
}

/**
 * Resolve any supported Vimeo URL to { id, hash } (hash null for public
 * videos). Numeric links resolve synchronously-fast (no network); share
 * links go through oEmbed. Returns null for unrecognized links.
 */
export async function resolveVimeoVideo(url) {
    const id = getVimeoVideoId(url);
    if (id) {
        return { id, hash: getVimeoUnlistedHash(url) };
    }

    return resolveVimeoShareUrl(url);
}
